import { useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Used two ways: a blocking full-screen when an admin reset forces a change
// (`forced`), and a dismissible modal for a voluntary self-change.
export function ChangePassword({ forced, onClose }: { forced?: boolean; onClose?: () => void }) {
  const { refresh } = useAuth();
  const { t } = useLang();
  const [cur, setCur] = useState('');
  const [np, setNp] = useState('');
  const [cf, setCf] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr('');
    if (np.length < 8) { setErr(t({ en: 'New password must be at least 8 characters.', fil: 'Ang bagong password ay dapat hindi bababa sa 8 character.' })); return; }
    if (np !== cf) { setErr(t({ en: 'The two new passwords don’t match.', fil: 'Hindi magkatugma ang dalawang bagong password.' })); return; }
    setBusy(true);
    try {
      await api.changePassword({ current_password: forced ? undefined : cur, new_password: np });
      await refresh();           // clears must_change; closes the forced screen
      if (onClose) onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : t({ en: 'Could not change password', fil: 'Hindi mapalitan ang password' })); }
    finally { setBusy(false); }
  }

  const card = (
    <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: '92vw' }}>
      <h2 style={{ marginTop: 0 }}>{forced ? t({ en: 'Set a new password', fil: 'Magtakda ng bagong password' }) : t(G.changePassword)}</h2>
      {forced && <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>{t({ en: 'An admin reset your password. Please choose a new one to continue.', fil: 'Ni-reset ng isang Admin ang iyong password. Pumili ng bago para magpatuloy.' })}</p>}
      {!forced && <Field label={t({ en: 'Current password', fil: 'Kasalukuyang password' })}><input type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" /></Field>}
      <Field label={t({ en: 'New password (at least 8 characters)', fil: 'Bagong password (hindi bababa sa 8 character)' })}><input type="password" value={np} onChange={(e) => setNp(e.target.value)} autoComplete="new-password" /></Field>
      <Field label={t({ en: 'Confirm new password', fil: 'Kumpirmahin ang bagong password' })}><input type="password" value={cf} onChange={(e) => setCf(e.target.value)} autoComplete="new-password" /></Field>
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        {!forced && onClose && <button className="btn btn-ghost" onClick={onClose}>{t(G.cancel)}</button>}
        <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? t(G.saving) : t({ en: 'Save password', fil: 'I-save ang password' })}</button>
      </div>
    </div>
  );

  return (
    <div onClick={forced ? undefined : onClose}
      style={{ position: 'fixed', inset: 0, background: forced ? 'var(--gray-50, #f4f4f4)' : 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      {card}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div style={{ marginBottom: 10 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{label}</label>{children}</div>;
}
